import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import api, { formatDate, formatCurrency, getErrorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  EnhancedTable,
  StickyTableHeader,
  SortableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
  SavedFiltersDropdown,
  TableSettingsDropdown,
} from '../components/ui/enhanced-table';
import { useTableSort, useSavedFilters, useTableSettings } from '../hooks/useTableEnhancements';
import { Package, Plus, Pencil, Trash2, AlertTriangle, ArrowDownCircle, ArrowUpCircle, RefreshCw, Search, Warehouse, History, X, Download, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { DATE_PRESETS, getPresetDates, downloadCsvFile } from '../lib/dateRange';

const UNITS = ['pcs', 'kg', 'm', 'L', 'set', 'box', 'roll'];
const CATEGORIES = ['Electrical', 'Mechanical', 'HVAC', 'Plumbing', 'General', 'Safety', 'Cleaning'];
const MOVEMENT_TYPES = ['IN', 'OUT', 'RETURN', 'ADJUST'];
const VALID_TABS = ['parts', 'stock', 'movements', 'lowstock'];
const MOVEMENT_PRESETS = [...DATE_PRESETS, { key: 'custom', label: 'Custom Range' }];

export default function InventoryPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canOpenWorkOrder = user?.role !== 'requester';
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(VALID_TABS.includes(urlTab) ? urlTab : 'parts');

  // Keep the active tab in the URL so a hard refresh returns to the same tab
  const changeTab = (value) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    if (!VALID_TABS.includes(searchParams.get('tab'))) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line
  const [parts, setParts] = useState([]);
  const [stock, setStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [fBranch, setFBranch] = useState('all');
  const [fCategory, setFCategory] = useState('all');
  const [fSearch, setFSearch] = useState('');
  const [fMovementType, setFMovementType] = useState('all');

  // Sorting with persistence
  const partsSort = useTableSort('inv_parts', 'name', 'asc');
  const stockSort = useTableSort('inv_stock', 'part_name', 'asc');
  const movementsSort = useTableSort('inv_movements', 'created_at', 'desc');
  const lowStockSort = useTableSort('inv_lowstock', 'shortage', 'desc');

  // Saved filters
  const { savedFilters, saveCurrentFilters, saveFilterPreset, deleteFilterPreset, loadCurrentFilters } = useSavedFilters('inv_page');

  // Column visibility and density for Parts tab
  const partsColumns = [
    { key: 'name', label: t('inventory.partName') || 'Part Name' },
    { key: 'sku', label: t('inventory.sku') || 'SKU' },
    { key: 'branch', label: t('common.branch') },
    { key: 'category', label: t('inventory.category') || 'Category' },
    { key: 'unit', label: t('inventory.unit') || 'Unit' },
    { key: 'unit_cost', label: t('inventory.unitCost') || 'Unit Cost' },
    { key: 'status', label: t('common.status') },
    { key: 'actions', label: t('common.actions') },
  ];
  const partsSettings = useTableSettings('inv_parts_cols', partsColumns, ['name', 'sku', 'branch']);

  // Column visibility and density for Stock tab
  const stockColumns = [
    { key: 'part_name', label: t('inventory.partName') || 'Part' },
    { key: 'sku', label: t('inventory.sku') },
    { key: 'branch', label: t('common.branch') },
    { key: 'qty_on_hand', label: t('inventory.qtyOnHand') || 'Qty' },
    { key: 'unit_cost', label: t('inventory.unitCost') },
    { key: 'stock_value', label: t('inventory.stockValue', 'Stock Value') },
    { key: 'reorder_point', label: t('inventory.reorderPoint') },
    { key: 'updated_at', label: t('common.updatedAt') },
  ];
  const stockLocked = ['part_name', 'sku', 'qty_on_hand', 'branch', 'unit_cost', 'stock_value'];
  const stockSettings = useTableSettings('inv_stock_cols', stockColumns, stockLocked);

  // Column visibility and density for Movements tab
  const movementsColumns = [
    { key: 'created_at', label: t('common.date') || 'Date' },
    { key: 'part_name', label: t('inventory.partName') || 'Part' },
    { key: 'branch', label: t('common.branch') },
    { key: 'type', label: t('common.type') || 'Type' },
    { key: 'qty', label: t('inventory.qty') || 'Qty' },
    { key: 'unit_cost', label: t('inventory.unitCost') },
    { key: 'total_cost', label: t('inventory.totalCost') },
    { key: 'work_order', label: t('nav.workOrders') },
    { key: 'created_by', label: t('common.createdBy') || 'By' },
    { key: 'reason', label: t('inventory.reason') || 'Reason' },
  ];
  const movementsLocked = ['created_at', 'type', 'qty', 'part_name', 'branch', 'unit_cost', 'total_cost', 'work_order', 'created_by'];
  const movementsSettings = useTableSettings('inv_movements_cols', movementsColumns, movementsLocked);

  // Column visibility and density for Low Stock tab
  const lowStockColumns = [
    { key: 'part_name', label: t('inventory.partName') || 'Part' },
    { key: 'sku', label: t('inventory.sku') },
    { key: 'branch', label: t('common.branch') },
    { key: 'qty_on_hand', label: t('inventory.qtyOnHand') || 'Qty' },
    { key: 'unit_cost', label: t('inventory.unitCost') },
    { key: 'stock_value', label: t('inventory.stockValue', 'Stock Value') },
    { key: 'reorder_point', label: t('inventory.reorderPoint') },
    { key: 'shortage', label: t('inventory.shortage') || 'Shortage' },
  ];
  const lowStockLocked = ['part_name', 'sku', 'qty_on_hand', 'reorder_point', 'branch', 'unit_cost', 'stock_value'];
  const lowStockSettings = useTableSettings('inv_lowstock_cols', lowStockColumns, lowStockLocked);

  // Part Dialog
  const [partDialogOpen, setPartDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [partForm, setPartForm] = useState({
    name: '', sku: '', category: 'General', branch_id: '', unit: 'pcs', unit_cost: '', is_active: true,
  });

  // Movement ledger filters (Asia/Riyadh dates) + pagination
  const [mvPreset, setMvPreset] = useState('all');
  const [mvFrom, setMvFrom] = useState('');
  const [mvTo, setMvTo] = useState('');
  const [mvSearch, setMvSearch] = useState('');
  const [mvWoRef, setMvWoRef] = useState('');
  const [mvPage, setMvPage] = useState(1);
  const [mvMeta, setMvMeta] = useState({ total: 0, total_pages: 1, page: 1, page_size: 25 });

  const movementParams = useCallback(() => {
    const params = {};
    if (fBranch !== 'all') params.branch_id = fBranch;
    if (fMovementType !== 'all') params.movement_type = fMovementType;
    if (mvSearch.trim()) params.search = mvSearch.trim();
    if (mvWoRef.trim()) params.work_order_ref = mvWoRef.trim();
    if (mvPreset === 'custom') {
      if (mvFrom) params.date_from = mvFrom;
      if (mvTo) params.date_to = mvTo;
    } else {
      const { from, to } = getPresetDates(mvPreset);
      if (from) params.date_from = from;
      if (to) params.date_to = to;
    }
    return params;
  }, [fBranch, fMovementType, mvSearch, mvWoRef, mvPreset, mvFrom, mvTo]);

  // Movement Dialog
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [movementForm, setMovementForm] = useState({
    part_id: '', branch_id: '', movement_type: 'IN', qty: '', reason: '',
  });

  // Load saved filters on mount
  useEffect(() => {
    const saved = loadCurrentFilters();
    if (saved) {
      if (saved.branch) setFBranch(saved.branch);
      if (saved.category) setFCategory(saved.category);
      if (saved.movementType) setFMovementType(saved.movementType);
    }
  }, []);

  // Save current filters when they change
  useEffect(() => {
    saveCurrentFilters({ branch: fBranch, category: fCategory, tab: activeTab, movementType: fMovementType });
  }, [fBranch, fCategory, activeTab, fMovementType, saveCurrentFilters]);

  const loadAll = useCallback(async () => {
    try {
      const params = {};
      if (fCategory !== 'all') params.category = fCategory;
      if (fBranch !== 'all') params.branch_id = fBranch;
      const [partsRes, branchesRes] = await Promise.all([
        api.get('/inventory/parts', { params }),
        api.get('/branches'),
      ]);
      setParts(partsRes.data);
      setBranches(branchesRes.data);
    } catch (err) {
      toast.error(t('toast.error'));
    } finally {
      setLoading(false);
    }
  }, [fCategory, fBranch, t]);

  const loadStock = useCallback(async () => {
    try {
      const params = {};
      if (fBranch !== 'all') params.branch_id = fBranch;
      const res = await api.get('/inventory/stock', { params });
      setStock(res.data);
    } catch (err) { console.error(err); }
  }, [fBranch]);

  const loadMovements = useCallback(async () => {
    try {
      const res = await api.get('/inventory/movements', {
        params: { ...movementParams(), page: mvPage, page_size: 25 },
      });
      setMovements(res.data.data);
      setMvMeta({
        total: res.data.total,
        total_pages: res.data.total_pages,
        page: res.data.page,
        page_size: res.data.page_size,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    }
  }, [movementParams, mvPage, t]);

  const loadLowStock = useCallback(async () => {
    try {
      const params = {};
      if (fBranch !== 'all') params.branch_id = fBranch;
      const res = await api.get('/inventory/low-stock', { params });
      setLowStock(res.data);
    } catch (err) { console.error(err); }
  }, [fBranch]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadStock(); loadLowStock(); loadMovements(); }, []);
  useEffect(() => { if (activeTab === 'stock') loadStock(); }, [activeTab, loadStock, fBranch]);
  useEffect(() => { if (activeTab === 'movements') loadMovements(); }, [activeTab, loadMovements]);
  useEffect(() => { if (activeTab === 'lowstock') loadLowStock(); }, [activeTab, loadLowStock, fBranch]);
  // Any filter change restarts at page 1
  useEffect(() => { setMvPage(1); }, [fBranch, fMovementType, mvSearch, mvWoRef, mvPreset, mvFrom, mvTo]);

  const exportCsv = async (endpoint, params) => {
    try {
      const { data } = await api.get(endpoint, { params });
      downloadCsvFile(data.csv, data.filename);
      toast.success(t('common.exported', 'Exported'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    }
  };

  const openPartDialog = (part = null) => {
    setEditingPart(part);
    setPartForm(part ? {
      name: part.name, sku: part.sku || '', category: part.category, branch_id: part.branch_id || '',
      unit: part.unit, unit_cost: part.unit_cost || '', is_active: part.is_active,
    } : {
      name: '', sku: '', category: 'General',
      branch_id: branches.length === 1 ? branches[0].id : (fBranch !== 'all' ? fBranch : ''),
      unit: 'pcs', unit_cost: '', is_active: true,
    });
    setPartDialogOpen(true);
  };

  const handlePartSubmit = async (e) => {
    e.preventDefault();
    if (!partForm.branch_id) {
      toast.error(t('inventory.branchRequired'));
      return;
    }
    try {
      const payload = {
        ...partForm,
        unit_cost: partForm.unit_cost ? parseFloat(partForm.unit_cost) : null,
      };
      if (editingPart) {
        await api.put(`/inventory/parts/${editingPart.id}`, payload);
      } else {
        await api.post('/inventory/parts', payload);
      }
      toast.success(t('toast.saved'));
      setPartDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(getErrorMessage(err, t('toast.error'), user));
    }
  };

  const handleDeletePart = async (partId) => {
    if (!window.confirm(t('common.confirm') + '?')) return;
    try {
      await api.delete(`/inventory/parts/${partId}`);
      toast.success(t('toast.deleted'));
      loadAll();
    } catch (err) {
      toast.error(t('toast.error'));
    }
  };

  const openMovementDialog = (type = 'IN', partId = '') => {
    const part = parts.find(p => p.id === partId);
    setMovementForm({
      part_id: partId,
      branch_id: part?.branch_id || (branches.length === 1 ? branches[0].id : ''),
      movement_type: type,
      qty: '',
      reason: '',
    });
    setMovementDialogOpen(true);
  };

  const handleMovementSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/inventory/movements', {
        ...movementForm,
        qty: parseInt(movementForm.qty),
      });
      toast.success(t('toast.saved'));
      setMovementDialogOpen(false);
      loadStock();
      loadMovements();
      loadLowStock();
    } catch (err) {
      toast.error(getErrorMessage(err, t('toast.error'), user));
    }
  };

  // A part belongs to one branch, so the movement branch follows the selected part
  const selectedMovementPart = useMemo(
    () => parts.find(p => p.id === movementForm.part_id),
    [parts, movementForm.part_id],
  );
  const movementBranchLocked = Boolean(selectedMovementPart?.branch_id);

  // Filter and sort parts
  const filteredParts = useMemo(() => {
    let result = parts.filter(p => 
      !fSearch || p.name.toLowerCase().includes(fSearch.toLowerCase()) || (p.sku && p.sku.toLowerCase().includes(fSearch.toLowerCase()))
    );
    return partsSort.sortData(result);
  }, [parts, fSearch, partsSort]);

  // Sort stock
  const sortedStock = useMemo(() => stockSort.sortData(stock), [stock, stockSort]);
  
  // Sort movements with movement type filter
  const sortedMovements = useMemo(() => {
    let result = movements;
    if (fMovementType !== 'all') {
      result = result.filter(m => m.movement_type === fMovementType);
    }
    return movementsSort.sortData(result);
  }, [movements, fMovementType, movementsSort]);
  
  // Sort low stock
  const sortedLowStock = useMemo(() => lowStockSort.sortData(lowStock), [lowStock, lowStockSort]);

  const clearFilters = () => {
    setFBranch('all');
    setFCategory('all');
    setFSearch('');
    setFMovementType('all');
  };

  const applyFilterPreset = (filters) => {
    if (filters.branch) setFBranch(filters.branch);
    if (filters.category) setFCategory(filters.category);
    if (filters.tab) setActiveTab(filters.tab);
    if (filters.movementType) setFMovementType(filters.movementType);
    toast.success(t('filters.applied'));
  };

  const hasActiveFilters = fBranch !== 'all' || fCategory !== 'all' || fSearch || fMovementType !== 'all';

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div data-testid="inventory-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{t('inventory.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('inventory.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SavedFiltersDropdown
            savedFilters={savedFilters}
            onSave={saveFilterPreset}
            onApply={applyFilterPreset}
            onDelete={deleteFilterPreset}
            currentFilters={{ branch: fBranch, category: fCategory, tab: activeTab, movementType: fMovementType }}
            t={t}
          />
          <Button onClick={() => openPartDialog()} className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="create-part-btn">
            <Plus className="w-4 h-4 me-2" /> {t('inventory.createPart')}
          </Button>
          <Button variant="outline" onClick={() => openMovementDialog('IN')} data-testid="receive-stock-btn">
            <ArrowDownCircle className="w-4 h-4 me-2" /> {t('inventory.receive')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{parts.length}</p>
              <p className="text-xs text-muted-foreground">{t('inventory.parts')}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <Warehouse className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stock.length}</p>
              <p className="text-xs text-muted-foreground">{t('inventory.stock')}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{lowStock.length}</p>
              <p className="text-xs text-muted-foreground">{t('inventory.lowStock')}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <History className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground" data-testid="stat-movements-total">{mvMeta.total}</p>
              <p className="text-xs text-muted-foreground">{t('inventory.movements')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Empty module state - no parts and no stock have been imported yet */}
      {!loading && parts.length === 0 && stock.length === 0 && (
        <Card className="bg-card border-dashed border-2 border-border" data-testid="inventory-empty-state">
          <CardContent className="p-8 text-center">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="font-barlow font-semibold text-base md:text-lg text-foreground">{t('inventory.emptyTitle')}</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl mx-auto">{t('inventory.emptyDesc')}</p>
            <p className="text-xs text-muted-foreground mt-2 max-w-xl mx-auto">{t('inventory.emptyHint')}</p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <Button
                onClick={() => openPartDialog()}
                className="bg-brand hover:bg-brand-hover text-brand-foreground"
                data-testid="inventory-empty-add-part-btn"
              >
                <Plus className="w-4 h-4 me-2" /> {t('inventory.addPart')}
              </Button>
              <Button variant="outline" onClick={() => openMovementDialog('IN')} data-testid="inventory-empty-receive-btn">
                <ArrowDownCircle className="w-4 h-4 me-2" /> {t('inventory.receiveOpeningStock')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={changeTab} className="space-y-4">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="parts" className="data-[state=active]:bg-card data-[state=active]:text-brand">
            <Package className="w-4 h-4 me-2" /> {t('inventory.parts')}
            <Badge variant="secondary" className="ms-2">{parts.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="stock" className="data-[state=active]:bg-card data-[state=active]:text-brand">
            <Warehouse className="w-4 h-4 me-2" /> {t('inventory.stock')}
          </TabsTrigger>
          <TabsTrigger value="movements" className="data-[state=active]:bg-card data-[state=active]:text-brand">
            <History className="w-4 h-4 me-2" /> {t('inventory.movements')}
          </TabsTrigger>
          <TabsTrigger value="lowstock" className="data-[state=active]:bg-card data-[state=active]:text-brand">
            <AlertTriangle className="w-4 h-4 me-2" /> {t('inventory.lowStock')}
            {lowStock.length > 0 && <Badge variant="secondary" className="ms-2 bg-amber-100 text-amber-700">{lowStock.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Parts Catalog Tab */}
        <TabsContent value="parts" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('common.search')}
                value={fSearch}
                onChange={(e) => setFSearch(e.target.value)}
                className="ps-9"
                data-testid="parts-search"
              />
              {fSearch && (
                <button onClick={() => setFSearch('')} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={fCategory} onValueChange={setFCategory}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="parts-category-filter">
                <SelectValue placeholder={t('inventory.category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {branches.length > 1 && (
              <Select value={fBranch} onValueChange={setFBranch}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="parts-branch-filter">
                  <SelectValue placeholder={t('common.branch')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <TableSettingsDropdown
              columns={partsColumns}
              lockedColumns={['name', 'sku']}
              density={partsSettings.density}
              onToggleColumn={partsSettings.toggleColumn}
              onSetDensity={partsSettings.setDensity}
              onResetAll={partsSettings.resetAll}
              isVisible={partsSettings.isVisible}
              isLocked={partsSettings.isLocked}
              t={t}
            />
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
              </Button>
            )}
          </div>

          <Card className="bg-card border-border shadow-sm">
            <CardContent className="p-0">
              <EnhancedTable stickyHeader>
                <StickyTableHeader>
                  <tr className="border-b border-border">
                    {partsSettings.isVisible('name') && (
                      <SortableHeader field="name" currentField={partsSort.sortField} currentDir={partsSort.sortDir} onSort={partsSort.toggleSort} className={partsSettings.densityClasses.header}>
                        {t('inventory.partName')}
                      </SortableHeader>
                    )}
                    {partsSettings.isVisible('sku') && (
                      <SortableHeader field="sku" currentField={partsSort.sortField} currentDir={partsSort.sortDir} onSort={partsSort.toggleSort} className={partsSettings.densityClasses.header}>
                        {t('inventory.sku')}
                      </SortableHeader>
                    )}
                    {partsSettings.isVisible('branch') && (
                      <SortableHeader field="branch_name" currentField={partsSort.sortField} currentDir={partsSort.sortDir} onSort={partsSort.toggleSort} className={partsSettings.densityClasses.header}>
                        {t('common.branch')}
                      </SortableHeader>
                    )}
                    {partsSettings.isVisible('category') && (
                      <SortableHeader field="category" currentField={partsSort.sortField} currentDir={partsSort.sortDir} onSort={partsSort.toggleSort} className={partsSettings.densityClasses.header}>
                        {t('inventory.category')}
                      </SortableHeader>
                    )}
                    {partsSettings.isVisible('unit') && (
                      <TableHeader className={partsSettings.densityClasses.header}>{t('inventory.unit')}</TableHeader>
                    )}
                    {partsSettings.isVisible('unit_cost') && (
                      <SortableHeader field="unit_cost" currentField={partsSort.sortField} currentDir={partsSort.sortDir} onSort={partsSort.toggleSort} className={partsSettings.densityClasses.header}>
                        {t('inventory.unitCost')}
                      </SortableHeader>
                    )}
                    {partsSettings.isVisible('status') && (
                      <TableHeader className={partsSettings.densityClasses.header}>{t('common.status')}</TableHeader>
                    )}
                    {partsSettings.isVisible('actions') && (
                      <TableHeader align="end" className={partsSettings.densityClasses.header}>{t('common.actions')}</TableHeader>
                    )}
                  </tr>
                </StickyTableHeader>
                <EnhancedTableBody>
                  {filteredParts.length === 0 && (
                    <TableEmptyState icon={Package} title={t('inventory.noParts')} colSpan={partsSettings.visibleColumns.length} />
                  )}
                  {filteredParts.map(part => (
                    <EnhancedTableRow key={part.id} data-testid={`part-row-${part.id}`}>
                      {partsSettings.isVisible('name') && (
                        <EnhancedTableCell className={`font-medium text-foreground ${partsSettings.densityClasses.cell}`}>{part.name}</EnhancedTableCell>
                      )}
                      {partsSettings.isVisible('sku') && (
                        <EnhancedTableCell className={`font-mono text-xs text-muted-foreground ${partsSettings.densityClasses.cell}`}>{part.sku || '—'}</EnhancedTableCell>
                      )}
                      {partsSettings.isVisible('branch') && (
                        <EnhancedTableCell className={partsSettings.densityClasses.cell} data-testid={`part-branch-${part.id}`}>
                          {part.branch_name || (
                            <Badge variant="outline" className="border-amber-400 text-amber-600 dark:text-amber-400">
                              {t('inventory.unassignedBranch')}
                            </Badge>
                          )}
                        </EnhancedTableCell>
                      )}
                      {partsSettings.isVisible('category') && (
                        <EnhancedTableCell className={partsSettings.densityClasses.cell}><Badge variant="outline">{part.category}</Badge></EnhancedTableCell>
                      )}
                      {partsSettings.isVisible('unit') && (
                        <EnhancedTableCell className={`text-muted-foreground ${partsSettings.densityClasses.cell}`}>{part.unit}</EnhancedTableCell>
                      )}
                      {partsSettings.isVisible('unit_cost') && (
                        <EnhancedTableCell className={`text-muted-foreground ${partsSettings.densityClasses.cell}`}>{part.unit_cost ? formatCurrency(part.unit_cost) : '—'}</EnhancedTableCell>
                      )}
                      {partsSettings.isVisible('status') && (
                        <EnhancedTableCell className={partsSettings.densityClasses.cell}>
                          <Badge variant={part.is_active ? 'default' : 'secondary'} className={part.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : ''}>
                            {part.is_active ? t('common.active') : t('common.inactive')}
                          </Badge>
                        </EnhancedTableCell>
                      )}
                      {partsSettings.isVisible('actions') && (
                        <EnhancedTableCell align="end" className={partsSettings.densityClasses.cell}>
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openPartDialog(part)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeletePart(part.id)} className="text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </EnhancedTableCell>
                      )}
                    </EnhancedTableRow>
                  ))}
                </EnhancedTableBody>
              </EnhancedTable>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stock Levels Tab */}
        <TabsContent value="stock" className="space-y-4">
          <div className="flex items-center gap-3">
            <Select value={fBranch} onValueChange={setFBranch}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="stock-branch-filter">
                <SelectValue placeholder={t('common.branch')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <TableSettingsDropdown
              columns={stockColumns}
              lockedColumns={stockLocked}
              density={stockSettings.density}
              onToggleColumn={stockSettings.toggleColumn}
              onSetDensity={stockSettings.setDensity}
              onResetAll={stockSettings.resetAll}
              isVisible={stockSettings.isVisible}
              isLocked={stockSettings.isLocked}
              t={t}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportCsv('/inventory/export/stock/csv', fBranch !== 'all' ? { branch_id: fBranch } : {})}
              data-testid="export-stock-csv"
            >
              <Download className="w-3.5 h-3.5 me-1" /> CSV
            </Button>
          </div>

          <Card className="bg-card border-border shadow-sm">
            <CardContent className="p-0">
              <EnhancedTable stickyHeader>
                <StickyTableHeader>
                  <tr className="border-b border-border">
                    {stockSettings.isVisible('part_name') && (
                      <SortableHeader field="part_name" currentField={stockSort.sortField} currentDir={stockSort.sortDir} onSort={stockSort.toggleSort} className={stockSettings.densityClasses.header}>
                        {t('inventory.partName')}
                      </SortableHeader>
                    )}
                    {stockSettings.isVisible('sku') && (
                      <SortableHeader field="part_sku" currentField={stockSort.sortField} currentDir={stockSort.sortDir} onSort={stockSort.toggleSort} className={stockSettings.densityClasses.header}>
                        {t('inventory.sku')}
                      </SortableHeader>
                    )}
                    {stockSettings.isVisible('branch') && (
                      <SortableHeader field="branch_name" currentField={stockSort.sortField} currentDir={stockSort.sortDir} onSort={stockSort.toggleSort} className={stockSettings.densityClasses.header}>
                        {t('common.branch')}
                      </SortableHeader>
                    )}
                    {stockSettings.isVisible('qty_on_hand') && (
                      <SortableHeader field="qty_on_hand" currentField={stockSort.sortField} currentDir={stockSort.sortDir} onSort={stockSort.toggleSort} className={stockSettings.densityClasses.header}>
                        {t('inventory.qtyOnHand')}
                      </SortableHeader>
                    )}
                    {stockSettings.isVisible('unit_cost') && (
                      <SortableHeader field="unit_cost" currentField={stockSort.sortField} currentDir={stockSort.sortDir} onSort={stockSort.toggleSort} className={`text-end ${stockSettings.densityClasses.header}`}>
                        {t('inventory.unitCost')}
                      </SortableHeader>
                    )}
                    {stockSettings.isVisible('stock_value') && (
                      <SortableHeader field="stock_value" currentField={stockSort.sortField} currentDir={stockSort.sortDir} onSort={stockSort.toggleSort} className={`text-end ${stockSettings.densityClasses.header}`}>
                        {t('inventory.stockValue', 'Stock Value')}
                      </SortableHeader>
                    )}
                    {stockSettings.isVisible('reorder_point') && (
                      <SortableHeader field="reorder_point" currentField={stockSort.sortField} currentDir={stockSort.sortDir} onSort={stockSort.toggleSort} className={stockSettings.densityClasses.header}>
                        {t('inventory.reorderPoint')}
                      </SortableHeader>
                    )}
                    <TableHeader className={stockSettings.densityClasses.header}>{t('common.status')}</TableHeader>
                  </tr>
                </StickyTableHeader>
                <EnhancedTableBody>
                  {sortedStock.length === 0 && (
                    <TableEmptyState icon={Warehouse} title={t('inventory.noStock')} colSpan={stockSettings.visibleColumns.length + 1} />
                  )}
                  {sortedStock.map(s => (
                    <EnhancedTableRow key={`${s.part_id}-${s.branch_id}`} isHighlighted={s.is_low_stock}>
                      {stockSettings.isVisible('part_name') && (
                        <EnhancedTableCell className={stockSettings.densityClasses.cell}>
                          <div>
                            <p className="font-medium text-foreground">{s.part_name}</p>
                            <p className="text-xs text-muted-foreground">{s.part_sku}</p>
                          </div>
                        </EnhancedTableCell>
                      )}
                      {stockSettings.isVisible('sku') && (
                        <EnhancedTableCell className={`font-mono text-xs text-muted-foreground ${stockSettings.densityClasses.cell}`} data-testid={`stock-sku-${s.part_id}-${s.branch_id}`}>{s.part_sku || '—'}</EnhancedTableCell>
                      )}
                      {stockSettings.isVisible('branch') && (
                        <EnhancedTableCell className={`text-muted-foreground ${stockSettings.densityClasses.cell}`} data-testid={`stock-branch-${s.part_id}-${s.branch_id}`}>{s.branch_name}</EnhancedTableCell>
                      )}
                      {stockSettings.isVisible('qty_on_hand') && (
                        <EnhancedTableCell className={stockSettings.densityClasses.cell} data-testid={`stock-qty-${s.part_id}-${s.branch_id}`}>
                          <span className={`font-bold ${s.is_low_stock ? 'text-amber-600' : 'text-foreground'}`}>
                            {s.qty_on_hand}
                          </span>
                          <span className="text-muted-foreground text-xs ms-1">{s.part_unit}</span>
                        </EnhancedTableCell>
                      )}
                      {stockSettings.isVisible('unit_cost') && (
                        <EnhancedTableCell className={`text-end ${stockSettings.densityClasses.cell}`} data-testid={`stock-unit-cost-${s.part_id}-${s.branch_id}`}>
                          {s.unit_cost != null ? formatCurrency(s.unit_cost) : '—'}
                        </EnhancedTableCell>
                      )}
                      {stockSettings.isVisible('stock_value') && (
                        <EnhancedTableCell className={`text-end font-medium ${stockSettings.densityClasses.cell}`} data-testid={`stock-value-${s.part_id}-${s.branch_id}`}>
                          {s.stock_value != null ? formatCurrency(s.stock_value) : '—'}
                        </EnhancedTableCell>
                      )}
                      {stockSettings.isVisible('reorder_point') && (
                        <EnhancedTableCell className={`text-muted-foreground ${stockSettings.densityClasses.cell}`}>{s.reorder_point}</EnhancedTableCell>
                      )}
                      <EnhancedTableCell className={stockSettings.densityClasses.cell}>
                        {s.is_low_stock ? (
                          <Badge variant="destructive" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            <AlertTriangle className="w-3 h-3 me-1" /> {t('inventory.lowStock')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">OK</Badge>
                        )}
                      </EnhancedTableCell>
                    </EnhancedTableRow>
                  ))}
                </EnhancedTableBody>
              </EnhancedTable>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Movements Tab */}
        <TabsContent value="movements" className="space-y-4">
          <div className="flex items-center gap-3">
            <Select value={fBranch} onValueChange={setFBranch}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="movements-branch-filter">
                <SelectValue placeholder={t('common.branch')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fMovementType} onValueChange={setFMovementType}>
              <SelectTrigger className="w-full sm:w-[150px]" data-testid="movements-type-filter">
                <SelectValue placeholder={t('inventory.movementType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                {MOVEMENT_TYPES.map(mt => <SelectItem key={mt} value={mt}>{t(`inventory.${mt.toLowerCase()}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={mvPreset} onValueChange={setMvPreset}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="movements-date-preset">
                <CalendarDays className="w-3.5 h-3.5 me-1.5 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MOVEMENT_PRESETS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <TableSettingsDropdown
              columns={movementsColumns}
              lockedColumns={movementsLocked}
              density={movementsSettings.density}
              onToggleColumn={movementsSettings.toggleColumn}
              onSetDensity={movementsSettings.setDensity}
              onResetAll={movementsSettings.resetAll}
              isVisible={movementsSettings.isVisible}
              isLocked={movementsSettings.isLocked}
              t={t}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportCsv('/inventory/export/movements/csv', movementParams())}
              data-testid="export-movements-csv"
            >
              <Download className="w-3.5 h-3.5 me-1" /> {t('inventory.ledgerCsv', 'Ledger CSV')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const { movement_type, work_order_ref, ...rest } = movementParams();
                exportCsv('/inventory/export/usage/csv', rest);
              }}
              data-testid="export-usage-csv"
            >
              <Download className="w-3.5 h-3.5 me-1" /> {t('inventory.usageCsv', 'Parts Usage CSV')}
            </Button>
            {(fBranch !== 'all' || fMovementType !== 'all' || mvPreset !== 'all' || mvSearch || mvWoRef) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFBranch('all'); setFMovementType('all'); setMvPreset('all'); setMvFrom(''); setMvTo(''); setMvSearch(''); setMvWoRef(''); }}
                className="text-muted-foreground"
                data-testid="movements-reset-filters"
              >
                <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {mvPreset === 'custom' && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">{t('common.from', 'From')}</Label>
                  <Input type="date" value={mvFrom} onChange={(e) => setMvFrom(e.target.value)} className="w-[150px]" data-testid="movements-date-from" />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">{t('common.to', 'To')}</Label>
                  <Input type="date" value={mvTo} onChange={(e) => setMvTo(e.target.value)} className="w-[150px]" data-testid="movements-date-to" />
                </div>
              </>
            )}
            <Input
              placeholder={t('inventory.searchPartSku', 'Search part or SKU')}
              value={mvSearch}
              onChange={(e) => setMvSearch(e.target.value)}
              className="w-full sm:w-[220px]"
              data-testid="movements-search"
            />
            <Input
              placeholder={t('inventory.woRef', 'Work Order ref')}
              value={mvWoRef}
              onChange={(e) => setMvWoRef(e.target.value)}
              className="w-full sm:w-[180px]"
              data-testid="movements-wo-filter"
            />
            <span className="text-xs text-muted-foreground ms-auto">Asia/Riyadh</span>
          </div>

          <Card className="bg-card border-border shadow-sm">
            <CardContent className="p-0">
              <EnhancedTable stickyHeader>
                <StickyTableHeader>
                  <tr className="border-b border-border">
                    {movementsSettings.isVisible('created_at') && (
                      <SortableHeader field="created_at" currentField={movementsSort.sortField} currentDir={movementsSort.sortDir} onSort={movementsSort.toggleSort} className={movementsSettings.densityClasses.header}>
                        {t('common.date')}
                      </SortableHeader>
                    )}
                    {movementsSettings.isVisible('part_name') && (
                      <SortableHeader field="part_name" currentField={movementsSort.sortField} currentDir={movementsSort.sortDir} onSort={movementsSort.toggleSort} className={movementsSettings.densityClasses.header}>
                        {t('inventory.partName')}
                      </SortableHeader>
                    )}
                    {movementsSettings.isVisible('branch') && (
                      <TableHeader className={movementsSettings.densityClasses.header}>{t('common.branch')}</TableHeader>
                    )}
                    {movementsSettings.isVisible('type') && (
                      <SortableHeader field="movement_type" currentField={movementsSort.sortField} currentDir={movementsSort.sortDir} onSort={movementsSort.toggleSort} className={movementsSettings.densityClasses.header}>
                        {t('inventory.movementType')}
                      </SortableHeader>
                    )}
                    {movementsSettings.isVisible('qty') && (
                      <SortableHeader field="qty" currentField={movementsSort.sortField} currentDir={movementsSort.sortDir} onSort={movementsSort.toggleSort} className={movementsSettings.densityClasses.header}>
                        {t('inventory.qty')}
                      </SortableHeader>
                    )}
                    {movementsSettings.isVisible('unit_cost') && (
                      <SortableHeader field="unit_cost" currentField={movementsSort.sortField} currentDir={movementsSort.sortDir} onSort={movementsSort.toggleSort} className={`text-end ${movementsSettings.densityClasses.header}`}>
                        {t('inventory.unitCost')}
                      </SortableHeader>
                    )}
                    {movementsSettings.isVisible('total_cost') && (
                      <SortableHeader field="total_cost" currentField={movementsSort.sortField} currentDir={movementsSort.sortDir} onSort={movementsSort.toggleSort} className={`text-end ${movementsSettings.densityClasses.header}`}>
                        {t('inventory.totalCost')}
                      </SortableHeader>
                    )}
                    {movementsSettings.isVisible('work_order') && (
                      <TableHeader className={movementsSettings.densityClasses.header}>{t('nav.workOrders')}</TableHeader>
                    )}
                    {movementsSettings.isVisible('reason') && (
                      <TableHeader className={movementsSettings.densityClasses.header}>{t('inventory.reason')}</TableHeader>
                    )}
                    {movementsSettings.isVisible('created_by') && (
                      <TableHeader className={movementsSettings.densityClasses.header}>{t('common.user')}</TableHeader>
                    )}
                  </tr>
                </StickyTableHeader>
                <EnhancedTableBody>
                  {sortedMovements.length === 0 && (
                    <TableEmptyState icon={History} title={t('inventory.noMovements')} colSpan={movementsSettings.visibleColumns.length} />
                  )}
                  {sortedMovements.map(m => (
                    <EnhancedTableRow key={m.id}>
                      {movementsSettings.isVisible('created_at') && (
                        <EnhancedTableCell className={`text-xs text-muted-foreground ${movementsSettings.densityClasses.cell}`}>{formatDate(m.created_at)}</EnhancedTableCell>
                      )}
                      {movementsSettings.isVisible('part_name') && (
                        <EnhancedTableCell className={movementsSettings.densityClasses.cell}>
                          <div>
                            <p className="font-medium text-foreground">{m.part_name}</p>
                            <p className="text-xs text-muted-foreground">{m.part_sku}</p>
                          </div>
                        </EnhancedTableCell>
                      )}
                      {movementsSettings.isVisible('branch') && (
                        <EnhancedTableCell className={`text-muted-foreground ${movementsSettings.densityClasses.cell}`}>{m.branch_name}</EnhancedTableCell>
                      )}
                      {movementsSettings.isVisible('type') && (
                        <EnhancedTableCell className={movementsSettings.densityClasses.cell}>
                          <Badge variant={m.movement_type === 'OUT' ? 'destructive' : m.movement_type === 'ADJUST' ? 'secondary' : 'default'}
                                 className={m.movement_type === 'OUT' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' : m.movement_type === 'ADJUST' ? '' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'}>
                            {(m.movement_type === 'IN' || m.movement_type === 'RETURN') && <ArrowDownCircle className="w-3 h-3 me-1" />}
                            {m.movement_type === 'OUT' && <ArrowUpCircle className="w-3 h-3 me-1" />}
                            {m.movement_type === 'ADJUST' && <RefreshCw className="w-3 h-3 me-1" />}
                            {t(`inventory.${m.movement_type.toLowerCase()}`)}
                          </Badge>
                        </EnhancedTableCell>
                      )}
                      {movementsSettings.isVisible('qty') && (
                        <EnhancedTableCell className={movementsSettings.densityClasses.cell}>
                          <span className={(m.movement_type === 'OUT' || m.qty < 0) ? 'text-rose-600' : 'text-emerald-600'}>
                            {m.movement_type === 'OUT' ? `-${Math.abs(m.qty)}` : m.qty > 0 ? `+${m.qty}` : m.qty}
                          </span>
                          <span className="text-muted-foreground text-xs ms-1">{m.part_unit}</span>
                        </EnhancedTableCell>
                      )}
                      {movementsSettings.isVisible('unit_cost') && (
                        <EnhancedTableCell className={`text-end ${movementsSettings.densityClasses.cell}`} data-testid={`movement-unit-cost-${m.id}`}>
                          {m.unit_cost != null ? formatCurrency(m.unit_cost) : (
                            <span className="text-xs text-muted-foreground" title={t('inventory.legacyCostHint', 'Recorded before cost snapshots existed')}>{t('inventory.legacy', 'legacy')}</span>
                          )}
                        </EnhancedTableCell>
                      )}
                      {movementsSettings.isVisible('total_cost') && (
                        <EnhancedTableCell className={`text-end font-medium ${movementsSettings.densityClasses.cell}`} data-testid={`movement-total-cost-${m.id}`}>
                          {m.total_cost != null ? formatCurrency(m.total_cost) : '—'}
                        </EnhancedTableCell>
                      )}
                      {movementsSettings.isVisible('work_order') && (
                        <EnhancedTableCell className={movementsSettings.densityClasses.cell} data-testid={`movement-wo-${m.id}`}>
                          {m.work_order_id ? (
                            canOpenWorkOrder ? (
                              <Link to={`/work-orders/${m.work_order_id}`} className="text-brand hover:underline font-mono text-xs" data-testid={`movement-wo-link-${m.id}`}>
                                {m.work_order_ref}
                              </Link>
                            ) : (
                              <span className="font-mono text-xs text-muted-foreground">{m.work_order_ref}</span>
                            )
                          ) : '—'}
                        </EnhancedTableCell>
                      )}
                      {movementsSettings.isVisible('reason') && (
                        <EnhancedTableCell className={`text-xs text-muted-foreground max-w-[150px] truncate ${movementsSettings.densityClasses.cell}`}>{m.reason || '—'}</EnhancedTableCell>
                      )}
                      {movementsSettings.isVisible('created_by') && (
                        <EnhancedTableCell className={`text-xs text-muted-foreground ${movementsSettings.densityClasses.cell}`}>{m.created_by_name}</EnhancedTableCell>
                      )}
                    </EnhancedTableRow>
                  ))}
                </EnhancedTableBody>
              </EnhancedTable>
            </CardContent>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
              <p className="text-xs text-muted-foreground" data-testid="movements-pagination-info">
                {mvMeta.total === 0
                  ? t('common.noResults', 'No results')
                  : `${(mvMeta.page - 1) * mvMeta.page_size + 1}–${Math.min(mvMeta.page * mvMeta.page_size, mvMeta.total)} / ${mvMeta.total}`}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={mvMeta.page <= 1}
                  onClick={() => setMvPage(p => Math.max(p - 1, 1))}
                  data-testid="movements-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-muted-foreground" data-testid="movements-page-label">
                  {mvMeta.page} / {mvMeta.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={mvMeta.page >= mvMeta.total_pages}
                  onClick={() => setMvPage(p => Math.min(p + 1, mvMeta.total_pages))}
                  data-testid="movements-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

          </Card>
        </TabsContent>

        {/* Low Stock Tab */}
        <TabsContent value="lowstock" className="space-y-4">
          <div className="flex items-center gap-3">
            <Select value={fBranch} onValueChange={setFBranch}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder={t('common.branch')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <TableSettingsDropdown
              columns={lowStockColumns}
              lockedColumns={lowStockLocked}
              density={lowStockSettings.density}
              onToggleColumn={lowStockSettings.toggleColumn}
              onSetDensity={lowStockSettings.setDensity}
              onResetAll={lowStockSettings.resetAll}
              isVisible={lowStockSettings.isVisible}
              isLocked={lowStockSettings.isLocked}
              t={t}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportCsv('/inventory/export/stock/csv', { low_stock_only: true, ...(fBranch !== 'all' ? { branch_id: fBranch } : {}) })}
              data-testid="export-lowstock-csv"
            >
              <Download className="w-3.5 h-3.5 me-1" /> CSV
            </Button>
          </div>

          <Card className="bg-card border-border shadow-sm">
            <CardContent className="p-0">
              <EnhancedTable stickyHeader>
                <StickyTableHeader>
                  <tr className="border-b border-border">
                    {lowStockSettings.isVisible('part_name') && (
                      <SortableHeader field="part_name" currentField={lowStockSort.sortField} currentDir={lowStockSort.sortDir} onSort={lowStockSort.toggleSort} className={lowStockSettings.densityClasses.header}>
                        {t('inventory.partName')}
                      </SortableHeader>
                    )}
                    {lowStockSettings.isVisible('sku') && (
                      <SortableHeader field="part_sku" currentField={lowStockSort.sortField} currentDir={lowStockSort.sortDir} onSort={lowStockSort.toggleSort} className={lowStockSettings.densityClasses.header}>
                        {t('inventory.sku')}
                      </SortableHeader>
                    )}
                    {lowStockSettings.isVisible('branch') && (
                      <SortableHeader field="branch_name" currentField={lowStockSort.sortField} currentDir={lowStockSort.sortDir} onSort={lowStockSort.toggleSort} className={lowStockSettings.densityClasses.header}>
                        {t('common.branch')}
                      </SortableHeader>
                    )}
                    {lowStockSettings.isVisible('qty_on_hand') && (
                      <SortableHeader field="qty_on_hand" currentField={lowStockSort.sortField} currentDir={lowStockSort.sortDir} onSort={lowStockSort.toggleSort} className={lowStockSettings.densityClasses.header}>
                        {t('inventory.qtyOnHand')}
                      </SortableHeader>
                    )}
                    {lowStockSettings.isVisible('unit_cost') && (
                      <SortableHeader field="unit_cost" currentField={lowStockSort.sortField} currentDir={lowStockSort.sortDir} onSort={lowStockSort.toggleSort} className={`text-end ${lowStockSettings.densityClasses.header}`}>
                        {t('inventory.unitCost')}
                      </SortableHeader>
                    )}
                    {lowStockSettings.isVisible('stock_value') && (
                      <SortableHeader field="stock_value" currentField={lowStockSort.sortField} currentDir={lowStockSort.sortDir} onSort={lowStockSort.toggleSort} className={`text-end ${lowStockSettings.densityClasses.header}`}>
                        {t('inventory.stockValue', 'Stock Value')}
                      </SortableHeader>
                    )}
                    {lowStockSettings.isVisible('reorder_point') && (
                      <TableHeader className={lowStockSettings.densityClasses.header}>{t('inventory.reorderPoint')}</TableHeader>
                    )}
                    {lowStockSettings.isVisible('shortage') && (
                      <SortableHeader field="shortage" currentField={lowStockSort.sortField} currentDir={lowStockSort.sortDir} onSort={lowStockSort.toggleSort} className={lowStockSettings.densityClasses.header}>
                        {t('inventory.shortage')}
                      </SortableHeader>
                    )}
                  </tr>
                </StickyTableHeader>
                <EnhancedTableBody>
                  {sortedLowStock.length === 0 && (
                    <TableEmptyState icon={AlertTriangle} title={t('inventory.noLowStock')} colSpan={lowStockSettings.visibleColumns.length} />
                  )}
                  {sortedLowStock.map(s => (
                    <EnhancedTableRow key={`${s.part_id}-${s.branch_id}`} isHighlighted>
                      {lowStockSettings.isVisible('part_name') && (
                        <EnhancedTableCell className={lowStockSettings.densityClasses.cell}>
                          <div>
                            <p className="font-medium text-foreground">{s.part_name}</p>
                            <p className="text-xs text-muted-foreground">{s.part_sku} • {s.part_category}</p>
                          </div>
                        </EnhancedTableCell>
                      )}
                      {lowStockSettings.isVisible('sku') && (
                        <EnhancedTableCell className={`font-mono text-xs text-muted-foreground ${lowStockSettings.densityClasses.cell}`} data-testid={`lowstock-sku-${s.part_id}-${s.branch_id}`}>{s.part_sku || '—'}</EnhancedTableCell>
                      )}
                      {lowStockSettings.isVisible('branch') && (
                        <EnhancedTableCell className={`text-muted-foreground ${lowStockSettings.densityClasses.cell}`} data-testid={`lowstock-branch-${s.part_id}-${s.branch_id}`}>{s.branch_name}</EnhancedTableCell>
                      )}
                      {lowStockSettings.isVisible('qty_on_hand') && (
                        <EnhancedTableCell className={`text-amber-600 font-bold ${lowStockSettings.densityClasses.cell}`}>{s.qty_on_hand} {s.part_unit}</EnhancedTableCell>
                      )}
                      {lowStockSettings.isVisible('unit_cost') && (
                        <EnhancedTableCell className={`text-end ${lowStockSettings.densityClasses.cell}`} data-testid={`lowstock-unit-cost-${s.part_id}-${s.branch_id}`}>
                          {s.unit_cost != null ? formatCurrency(s.unit_cost) : '—'}
                        </EnhancedTableCell>
                      )}
                      {lowStockSettings.isVisible('stock_value') && (
                        <EnhancedTableCell className={`text-end font-medium ${lowStockSettings.densityClasses.cell}`} data-testid={`lowstock-value-${s.part_id}-${s.branch_id}`}>
                          {s.stock_value != null ? formatCurrency(s.stock_value) : '—'}
                        </EnhancedTableCell>
                      )}
                      {lowStockSettings.isVisible('reorder_point') && (
                        <EnhancedTableCell className={`text-muted-foreground ${lowStockSettings.densityClasses.cell}`}>{s.reorder_point} {s.part_unit}</EnhancedTableCell>
                      )}
                      {lowStockSettings.isVisible('shortage') && (
                        <EnhancedTableCell className={lowStockSettings.densityClasses.cell}>
                          <Badge variant="destructive" className="bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
                            {s.shortage > 0 ? `-${s.shortage}` : 0} {s.part_unit}
                          </Badge>
                        </EnhancedTableCell>
                      )}
                    </EnhancedTableRow>
                  ))}
                </EnhancedTableBody>
              </EnhancedTable>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Part Dialog */}
      <Dialog open={partDialogOpen} onOpenChange={setPartDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPart ? t('inventory.editPart') : t('inventory.createPart')}</DialogTitle>
            <DialogDescription>{t('inventory.subtitle')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePartSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('common.branch')} *</Label>
              <Select value={partForm.branch_id} onValueChange={(v) => setPartForm({ ...partForm, branch_id: v })}>
                <SelectTrigger data-testid="part-branch-select">
                  <SelectValue placeholder={t('common.selectBranch')} />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground" data-testid="part-branch-hint">{t('inventory.branchOwnershipHint')}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('inventory.partName')} *</Label>
                <Input
                  value={partForm.name}
                  onChange={(e) => setPartForm({ ...partForm, name: e.target.value })}
                  required
                  data-testid="part-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('inventory.sku')}</Label>
                <Input
                  value={partForm.sku}
                  onChange={(e) => setPartForm({ ...partForm, sku: e.target.value })}
                  data-testid="part-sku-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('inventory.category')}</Label>
                <Select value={partForm.category} onValueChange={(v) => setPartForm({ ...partForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('inventory.unit')}</Label>
                <Select value={partForm.unit} onValueChange={(v) => setPartForm({ ...partForm, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('inventory.unitCost')} ({t('common.currency')})</Label>
              <Input
                type="number"
                step="0.01"
                value={partForm.unit_cost}
                onChange={(e) => setPartForm({ ...partForm, unit_cost: e.target.value })}
                data-testid="part-cost-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="part-active"
                checked={partForm.is_active}
                onCheckedChange={(checked) => setPartForm({ ...partForm, is_active: checked })}
              />
              <Label htmlFor="part-active">{t('common.active')}</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPartDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="part-submit-btn">
                {editingPart ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={movementDialogOpen} onOpenChange={setMovementDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {movementForm.movement_type === 'IN' ? t('inventory.receive') : 
               movementForm.movement_type === 'ADJUST' ? t('inventory.adjust') : t('inventory.issue')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMovementSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('inventory.partName')} *</Label>
              <Select value={movementForm.part_id} onValueChange={(v) => {
                const p = parts.find(x => x.id === v);
                setMovementForm({ ...movementForm, part_id: v, branch_id: p?.branch_id || movementForm.branch_id });
              }} required>
                <SelectTrigger data-testid="movement-part-select"><SelectValue placeholder={t('inventory.selectPart')} /></SelectTrigger>
                <SelectContent>
                  {parts.filter(p => p.is_active).map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku || 'no SKU'})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.branch')} *</Label>
                <Select
                  value={movementForm.branch_id}
                  onValueChange={(v) => setMovementForm({ ...movementForm, branch_id: v })}
                  disabled={movementBranchLocked}
                  required
                >
                  <SelectTrigger data-testid="movement-branch-select"><SelectValue placeholder={t('common.selectBranch')} /></SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {movementBranchLocked && (
                  <p className="text-xs text-muted-foreground" data-testid="movement-branch-locked-hint">{t('inventory.branchLocked')}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>{t('inventory.movementType')}</Label>
                <Select value={movementForm.movement_type} onValueChange={(v) => setMovementForm({ ...movementForm, movement_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IN">{t('inventory.in')}</SelectItem>
                    <SelectItem value="ADJUST">{t('inventory.adjust_type')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('inventory.qty')} *</Label>
              <Input
                type="number"
                min="1"
                value={movementForm.qty}
                onChange={(e) => setMovementForm({ ...movementForm, qty: e.target.value })}
                required
                data-testid="movement-qty-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('inventory.reason')}</Label>
              <Input
                value={movementForm.reason}
                onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })}
                placeholder={t('inventory.initialStock')}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMovementDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="movement-submit-btn">
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
